import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, Navigation, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth-context";
import { formatRands, shortId, statusLabel, DRIVER_NEXT } from "@/lib/order";
import { useRoom } from "@/hooks/use-room";
import { StaffShell } from "@/components/layout/StaffShell";
import { driverNav } from "@/lib/nav";
import { OrderMap } from "@/components/common/OrderMap";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CenterSpinner, EmptyState } from "@/components/common/States";

export default function DriverDashboard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const watchRef = useRef(null);

  const me = useQuery({ queryKey: ["driver-me"], queryFn: () => api("/drivers/me"), refetchInterval: 20_000 });
  const jobs = useQuery({ queryKey: ["driver-jobs"], queryFn: () => api("/drivers/jobs"), refetchInterval: 15_000 });

  useRoom(user ? `driver:${user.user_id}` : null, (e) => {
    if (e.event === "NEW_JOB") toast.info("New delivery available");
    qc.invalidateQueries({ queryKey: ["driver-jobs"] });
    qc.invalidateQueries({ queryKey: ["driver-me"] });
  });

  const online = !!me.data?.is_online;

  // Foreground live-location sharing while online (web supports this only in an open tab).
  useEffect(() => {
    if (online && "geolocation" in navigator && watchRef.current == null) {
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          api("/drivers/location", {
            method: "POST",
            json: { lat: pos.coords.latitude, lng: pos.coords.longitude, heading: pos.coords.heading, speed: pos.coords.speed },
          }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10_000 },
      );
    }
    if (!online && watchRef.current != null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    return () => {
      if (watchRef.current != null) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
    };
  }, [online]);

  const toggleOnline = (v) => {
    const send = (lat, lng) =>
      api("/drivers/online", { method: "POST", json: { is_online: v, lat, lng } })
        .then(() => qc.invalidateQueries({ queryKey: ["driver-me"] }))
        .catch((e) => toast.error(e.message));
    if (v && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos.coords.latitude, pos.coords.longitude),
        () => send(null, null),
      );
    } else {
      send(null, null);
    }
  };

  const accept = async (order) => {
    try {
      await api("/drivers/accept-job", { method: "POST", json: { order_id: order.order_id } });
      toast.success("Job accepted");
      qc.invalidateQueries({ queryKey: ["driver-me"] });
      qc.invalidateQueries({ queryKey: ["driver-jobs"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const advance = async (order) => {
    const next = DRIVER_NEXT[order.status];
    if (!next) return;
    try {
      await api(`/orders/${order.order_id}/status`, { method: "PATCH", json: { status: next } });
      qc.invalidateQueries({ queryKey: ["driver-me"] });
      toast.success(statusLabel(next));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const active = me.data?.active_order;
  const points = [];
  if (active) {
    points.push({ lat: active.vendor_lat, lng: active.vendor_lng, type: "vendor", label: active.vendor_name });
    points.push({ lat: active.delivery_lat, lng: active.delivery_lng, type: "customer", label: "Drop-off" });
  }
  if (me.data?.latitude != null) points.push({ lat: me.data.latitude, lng: me.data.longitude, type: "driver", label: "You" });

  return (
    <StaffShell items={driverNav} roleLabel="Driver" title="Deliveries">
      {me.isLoading ? (
        <CenterSpinner />
      ) : (
        <div className="space-y-5">
          <Card className="flex items-center justify-between rounded-2xl p-5">
            <div>
              <p className="font-display text-lg">{online ? "You're online" : "You're offline"}</p>
              <p className="text-sm text-muted-foreground">
                {online ? "Sharing live location · receiving jobs" : "Go online to receive delivery jobs"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="online-switch" className="text-sm">Online</Label>
              <Switch id="online-switch" checked={online} onCheckedChange={toggleOnline} data-testid="online-toggle" />
            </div>
          </Card>

          {points.length > 0 && <OrderMap points={points} height={300} />}

          {active ? (
            <Card className="rounded-2xl p-5" data-testid="active-delivery">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Active delivery</p>
                  <p className="font-display text-lg">{active.vendor_name}</p>
                  <p className="text-sm text-muted-foreground">{shortId(active.order_id)} · {formatRands(active.total)}</p>
                </div>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-sm text-primary">{statusLabel(active.status)}</span>
              </div>
              <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {active.delivery_address}
              </div>
              {DRIVER_NEXT[active.status] && (
                <Button className="mt-4 w-full rounded-full" onClick={() => advance(active)} data-testid="advance-delivery-button">
                  <Navigation className="mr-2 h-4 w-4" /> Mark {statusLabel(DRIVER_NEXT[active.status])}
                </Button>
              )}
            </Card>
          ) : (
            <div>
              <h2 className="mb-3 font-display text-lg">Available jobs</h2>
              {!online ? (
                <EmptyState icon={Package} title="You're offline" description="Go online to see available deliveries." />
              ) : !jobs.data?.length ? (
                <EmptyState icon={Package} title="No jobs right now" description="New deliveries will appear here automatically." />
              ) : (
                <div className="grid gap-3">
                  {jobs.data.map((j) => (
                    <Card key={j.order_id} className="flex items-center gap-4 rounded-2xl p-4" data-testid={`job-${j.order_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base">{j.vendor_name}</p>
                        <p className="text-sm text-muted-foreground">{shortId(j.order_id)} · payout ~{formatRands(j.delivery_fee * 0.8 + (j.tip || 0))}</p>
                      </div>
                      <Button className="rounded-full" onClick={() => accept(j)} data-testid={`accept-job-${j.order_id}`}>Accept</Button>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </StaffShell>
  );
}
